import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
	AUTHORIZATION_ID_PARAM,
	AUTHORIZATION_CANCELLED_RETURN_URL_KEY,
} from '@gov-nx/component/common/Autorizace/constants';
import {
	AuthorizationOutcome,
	resolveAuthorizationOutcome,
} from '@gov-nx/component/common/Autorizace/outcome';
import { useAuthorizationCleanup } from '@gov-nx/component/common/Autorizace/useAuthorizationCleanup';
import { useProcessSuccessMessage } from '@gov-nx/component/common/Autorizace/useProcessSuccessMessage';
import { useMessageEvents } from '@gov-nx/core/events/Message/useMessageEvents';
import { useLatest } from '@gov-nx/core/hooks/State/useLatest';
import { useLocale } from '@gov-nx/core/service/translations/useLocale';
import { isNonEmptyString } from '@gov-nx/core/types/helpers.types';
import type { PageCode } from '@gov-nx/module/page/definitions/codes';
import { useAutorizaceStore } from '@gov-nx/store/autorizace/store';
import { LayoutGap } from '@gov-nx/ui/web/Layout/LayoutGap';
import { useSessionStorage } from '@gov-nx/utils/web/hooks/useSessionStorage';
import { pageRouteResolver } from '@gov-nx/web/Router/routeResolver';
import { useFailedSubmissionRedirect } from '../../Autorizace/useFailedSubmissionRedirect';
import { resolveLandingUrl } from './landingRules';

export interface AutorizaceViewProps {
	code: (typeof PageCode)['autorizace'];
}

export const AutorizaceView = ({ code }: AutorizaceViewProps) => {
	const [searchParams] = useSearchParams();
	// NIA returns to /autorizace?Id={authId}&Result=1&Error=... — the branch is
	// decided from these params.
	const outcome = resolveAuthorizationOutcome((name) => searchParams.get(name));
	// Stored on authorization start; used to return the user to where they left
	// off, both on success and on user cancellation.
	const authorizationReturnUrl = useSessionStorage(
		AUTHORIZATION_CANCELLED_RETURN_URL_KEY
	);
	const clearAuthorization = useAutorizaceStore('clearAuthorization');
	const confirmPendingAuthorizedChange = useAutorizaceStore(
		'confirmPendingAuthorizedChange'
	);
	const startedUkonId = useAutorizaceStore('ukonId');
	// Set on start by launch sites that own their submission — the cancel must
	// leave it alone. See `onInitAuthorization`.
	const launchSiteHandlesCancel = useAutorizaceStore('launchSiteHandlesCancel');
	// authorizationId is persisted through AsyncStorage, which resolves
	// asynchronously even on web. Deciding the outcome before the store has
	// hydrated would read an empty id and never send dokoncit.
	const hasHydrated = useAutorizaceStore('_hasHydrated');
	const { finalizeAuthorization, resolveSubmissionId, deleteSubmission } =
		useAuthorizationCleanup();
	const resolveProcessSuccessMessage = useProcessSuccessMessage();
	const { redirectToFailedSubmission } = useFailedSubmissionRedirect();

	const navigate = useNavigate();
	const latestNavigate = useLatest(navigate);
	const { t } = useLocale(code);

	const hasChecked = useRef(false);

	const { toastMessageSuccess, toastMessageError } = useMessageEvents();

	useEffect(() => {
		if (!hasHydrated || hasChecked.current) {
			return;
		}
		hasChecked.current = true;

		const authId = searchParams.get(AUTHORIZATION_ID_PARAM);

		if (isNonEmptyString(startedUkonId) && authId !== startedUkonId) {
			latestNavigate.current(pageRouteResolver('dashboard'), { replace: true });
			return;
		}

		// Shared failure landing: resolve the temp submission id and redirect to its
		// detail with the failure banner (keeping the submission), else fall back to
		// the dashboard. Used by the Result=0 branch and by a non-OK dokoncit status
		// on the Result=1 branch.
		const redirectToFailedDetail = async () => {
			const detailId = await resolveSubmissionId(authId ?? undefined);
			clearAuthorization();
			authorizationReturnUrl.remove();
			toastMessageError(t('page.message.auth-fail'));
			redirectToFailedSubmission(detailId);
		};

		if (outcome === AuthorizationOutcome.Cancelled) {
			// Consume the return URL before we wipe local data, so the user lands
			// back where they started.
			const returnUrl =
				authorizationReturnUrl.value ?? pageRouteResolver('dashboard');

			const cancelByUser = async () => {
				if (!launchSiteHandlesCancel) {
					// Close the authorization on the BE first — isolated (best-effort),
					// so a rejected finalize cannot skip the cleanup below.
					await finalizeAuthorization();
					// Resolve the temporary submission id from the authId and remove it
					// — the same cleanup request as the "Zrušit formulář" button.
					await deleteSubmission(
						await resolveSubmissionId(authId ?? undefined)
					);
				}
				clearAuthorization();
				authorizationReturnUrl.remove();
				toastMessageError(t('page.message.auth-cancelled'));
				// Return the user to the place they started, except for the origins
				// declared in landingRules.
				latestNavigate.current(resolveLandingUrl('cancelled', returnUrl), {
					replace: true,
				});
			};

			void cancelByUser();
			return;
		}

		if (outcome === AuthorizationOutcome.Success) {
			// Consume the return URL before local data is wiped, so the user lands
			// back where they started.
			const returnUrl =
				authorizationReturnUrl.value ?? pageRouteResolver('moje-podani');

			const finishSuccess = async () => {
				const status = await finalizeAuthorization();
				// Result=1 is only NIA's half of the answer: without an explicit OK from
				// our own finalization the authorization is not confirmed, so a refusal,
				// a failed request and a missing status all land in the failure branch,
				// which keeps the submission.
				if (status !== 'OK') {
					await redirectToFailedDetail();
					return;
				}
				// Read before clearAuthorization below wipes the registered toast.
				const processMessage = await resolveProcessSuccessMessage();
				// The eID leg left the app, so the view that submitted the change is
				// gone; confirming the change here is what makes it wait for — and
				// re-read — the register write once it mounts again below.
				confirmPendingAuthorizedChange();
				clearAuthorization();
				authorizationReturnUrl.remove();
				toastMessageSuccess(processMessage ?? t('page.message.auth-success'));
				// Return the user to the place they started, no fixed landing page —
				// except for the origins declared in landingRules.
				latestNavigate.current(resolveLandingUrl('success', returnUrl), {
					replace: true,
				});
			};

			void finishSuccess();
			return;
		}

		if (outcome === AuthorizationOutcome.Failure) {
			// Authorization failed for a reason other than user cancellation. The
			// temporary submission is KEPT; we redirect the user to its detail in
			// the "Čeká na vyřízení" section and flag the failure so the detail can
			// show the "Autorizace se nezdařila" banner.
			const failByOtherReason = async () => {
				// Close the authorization on the BE first — isolated (best-effort), so
				// a rejected finalize cannot skip the detail resolution/redirect below.
				await finalizeAuthorization();
				await redirectToFailedDetail();
			};

			void failByOtherReason();
			return;
		}

		// No recognized NIA return params — send the user somewhere safe.
		latestNavigate.current(pageRouteResolver('dashboard'), { replace: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps -- runs once, on the first render after hydration; the NIA return params do not change within this view
	}, [hasHydrated]);

	return (
		<LayoutGap gap="xl">
			<h5>{t('page.authorization-loading')}</h5>
		</LayoutGap>
	);
};
